export const SELECTED_PROJECT_STORAGE_KEY = 'webcert:selected-project-id'
export const SELECTED_PROJECT_METADATA_STORAGE_KEY = 'webcert:selected-project-meta'
export const SELECTED_PROJECTS_STORAGE_KEY = 'webcert:selected-projects'
export const INCLUDE_ARCHIVED_PROJECTS_STORAGE_KEY = 'webcert:include-archived-projects'

export const PROJECT_SELECTED_EVENT = 'project-selector:change'
export const PROJECT_SELECTION_NAVIGATION_STATE_KEY = 'projectSelection'

export interface ProjectSelectionProject {
  id: number
  projectName: string
  projectNumber: string
  isMock: boolean
  isArchived?: boolean
  programmeName?: string | null
}

export interface ProjectSelectionEventDetail {
  projectId: number | null
  project?: ProjectSelectionProject
  projectIds?: number[]
  projects?: ProjectSelectionProject[]
}

export type ProjectSelectionNavigationState = {
  [PROJECT_SELECTION_NAVIGATION_STATE_KEY]?: ProjectSelectionEventDetail
}

export const readProjectSelectionFromNavigationState = (
  state: unknown
): ProjectSelectionEventDetail | null => {
  if (!state || typeof state !== 'object') {
    return null
  }

  const selection = (state as Record<string, unknown>)[PROJECT_SELECTION_NAVIGATION_STATE_KEY]
  if (!selection || typeof selection !== 'object') {
    return null
  }

  const detail = selection as ProjectSelectionEventDetail
  if (detail.projectId !== null && typeof detail.projectId !== 'number') {
    return null
  }

  return detail
}

export const readStoredProjectId = () => {
  if (typeof window === 'undefined') {
    return null
  }

  const rawValue = window.localStorage.getItem(SELECTED_PROJECT_STORAGE_KEY)
  if (!rawValue) {
    return null
  }

  const parsed = Number(rawValue)
  return Number.isFinite(parsed) ? parsed : null
}

export const readStoredProjectMetadata = () => {
  if (typeof window === 'undefined') {
    return {}
  }
  try {
    const raw = window.localStorage.getItem(SELECTED_PROJECT_METADATA_STORAGE_KEY)
    if (!raw) {
      return {}
    }
    const parsed = JSON.parse(raw) as Partial<ProjectSelectionEventDetail['project']>
    return parsed ?? {}
  } catch {
    return {}
  }
}

export const writeStoredProjectId = (projectId: number) => {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(SELECTED_PROJECT_STORAGE_KEY, projectId.toString())
}

export const writeStoredProjectMetadata = (metadata: Partial<ProjectSelectionEventDetail['project']>) => {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.setItem(SELECTED_PROJECT_METADATA_STORAGE_KEY, JSON.stringify(metadata))
  } catch {
    // ignore storage errors
  }
}

export const clearStoredProjectId = () => {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.removeItem(SELECTED_PROJECT_STORAGE_KEY)
  window.localStorage.removeItem(SELECTED_PROJECT_METADATA_STORAGE_KEY)
  window.localStorage.removeItem(SELECTED_PROJECTS_STORAGE_KEY)
}

export const clearStoredProjectMetadata = () => {
  if (typeof window === 'undefined') {
    return
  }
  window.localStorage.removeItem(SELECTED_PROJECT_METADATA_STORAGE_KEY)
}

export const dispatchProjectSelectionChange = (detail: ProjectSelectionEventDetail) => {
  if (typeof window === 'undefined') {
    return
  }

  const projects = resolveProjectSelection(detail)
  writeStoredProjectSelection(projects)
  window.dispatchEvent(
    new CustomEvent<ProjectSelectionEventDetail>(PROJECT_SELECTED_EVENT, {
      detail: createProjectSelectionDetail(projects),
    })
  )
}

const EMPTY_SELECTION: ProjectSelectionProject[] = []
let cachedSelection = EMPTY_SELECTION
let cachedSelectionKey: string | undefined

const normalizeProjects = (projects: ProjectSelectionProject[]): ProjectSelectionProject[] => {
  const unique = new Map<number, ProjectSelectionProject>()
  projects.forEach((project) => {
    if (!project || !Number.isSafeInteger(project.id) || project.id <= 0) return
    unique.set(project.id, {
      id: project.id,
      projectName: typeof project.projectName === 'string' ? project.projectName : 'Selected project',
      projectNumber: typeof project.projectNumber === 'string' ? project.projectNumber : '',
      programmeName: typeof project.programmeName === 'string' ? project.programmeName : null,
      isMock: Boolean(project.isMock), isArchived: Boolean(project.isArchived),
    })
  })
  return Array.from(unique.values())
}

export const resolveProjectSelection = (detail: ProjectSelectionEventDetail): ProjectSelectionProject[] => {
  if (Array.isArray(detail.projects)) return normalizeProjects(detail.projects)
  if (detail.projectId === null) return []
  const metadata = detail.project ?? readStoredProjectMetadata()
  return normalizeProjects([{
    id: detail.projectId, projectName: metadata.projectName ?? 'Selected project',
    projectNumber: metadata.projectNumber ?? '', isMock: Boolean(metadata.isMock),
    isArchived: metadata.isArchived, programmeName: metadata.programmeName,
  }])
}

export const readStoredProjectSelection = (): ProjectSelectionProject[] => {
  if (typeof window === 'undefined') return EMPTY_SELECTION
  const raw = window.localStorage.getItem(SELECTED_PROJECTS_STORAGE_KEY)
  const key = JSON.stringify([raw, window.localStorage.getItem(SELECTED_PROJECT_STORAGE_KEY), window.localStorage.getItem(SELECTED_PROJECT_METADATA_STORAGE_KEY)])
  if (key === cachedSelectionKey) return cachedSelection
  let projects: ProjectSelectionProject[] | undefined
  if (raw !== null) {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) projects = normalizeProjects(parsed)
    } catch { /* Fall back to the legacy single-project selection. */ }
  }
  const id = readStoredProjectId()
  cachedSelection = projects ?? (id === null ? [] : resolveProjectSelection({projectId: id}))
  cachedSelectionKey = key
  return cachedSelection
}

export const writeStoredProjectSelection = (projects: ProjectSelectionProject[]) => {
  if (typeof window === 'undefined') return
  const normalized = normalizeProjects(projects)
  window.localStorage.setItem(SELECTED_PROJECTS_STORAGE_KEY, JSON.stringify(normalized))
  if (normalized.length === 1 && !normalized[0].isMock) {
    writeStoredProjectId(normalized[0].id)
    writeStoredProjectMetadata(normalized[0])
  } else {
    window.localStorage.removeItem(SELECTED_PROJECT_STORAGE_KEY)
    window.localStorage.removeItem(SELECTED_PROJECT_METADATA_STORAGE_KEY)
  }
}

export const createProjectSelectionDetail = (projects: ProjectSelectionProject[]): ProjectSelectionEventDetail => ({
  projectId: projects.length === 1 ? projects[0].id : null,
  project: projects.length === 1 ? projects[0] : undefined,
  projectIds: projects.map((project) => project.id), projects,
})
